export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

const KEY = 'p2h.session';

/**
 * Sessions live in localStorage: the SPA talks to four separate service origins, so a
 * host-only cookie is not an option, and the tokens are short-lived with rotating
 * refresh. `sessionStorage` would drop the session on every new tab.
 */
export function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.accessToken || !parsed.refreshToken) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeSession(input: { accessToken: string; refreshToken: string; expiresIn: number }): StoredSession {
  const session: StoredSession = {
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    expiresAt: Date.now() + input.expiresIn * 1000,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(session));
  } catch {
    /* private browsing — the session simply will not survive a reload */
  }
  return session;
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clean up */
  }
}

/** Refresh a minute before expiry rather than after a 401. */
export function isExpiring(session: StoredSession | null): boolean {
  return !session || session.expiresAt - 60_000 < Date.now();
}

export interface AccessTokenClaims {
  sub: string;
  email?: string;
  exp: number;
  app_metadata?: { rbac_role?: string; company_id?: string };
  user_metadata?: { full_name?: string; avatar_url?: string };
}

/**
 * Reads claims for UI purposes only — which nav items to show, whose name to greet.
 * Every actual authorisation decision is made server-side against the signature.
 */
export function decodeClaims(token: string): AccessTokenClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json))) as AccessTokenClaims;
  } catch {
    return null;
  }
}
